import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@lms/shared-types';
import { ConfirmModal, Loader, useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { customFieldGroupSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';
import { groupDotClass } from '@/lib/user-custom-field-types';

interface FieldGroup {
  id: string;
  name: string;
  _count?: { fields?: number };
}

function fieldsCount(group: FieldGroup) {
  return group._count?.fields ?? 0;
}

function GroupRowActions({ group }: { group: FieldGroup }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(PERMISSIONS.CAMPOS_USUARIO_EDITAR);
  const canDelete = hasPermission(PERMISSIONS.CAMPOS_USUARIO_ELIMINAR);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const assigned = fieldsCount(group);

  const deleteMutation = useMutation({
    mutationFn: () => api(`/user-custom-fields/groups/${group.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleteOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['user-custom-field-groups'] });
      void queryClient.invalidateQueries({ queryKey: ['user-custom-fields'] });
      toast(customFieldGroupSuccessToast('delete'));
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo eliminar el grupo de campos'));
    },
  });

  return (
    <>
      <div className="flex items-center justify-end gap-1">
        {canEdit ? (
          <Link
            to={`/campos-personalizados/grupos/${group.id}/edit`}
            className="rounded-lg p-2 text-dash-on-surface-variant transition-colors hover:bg-brand-50 hover:text-dash-primary"
            title="Editar"
            aria-label={`Editar ${group.name}`}
          >
            <span className="material-symbols-outlined text-xl" aria-hidden="true">
              edit
            </span>
          </Link>
        ) : null}
        {canDelete ? (
          <button
            type="button"
            className="rounded-lg p-2 text-dash-on-surface-variant transition-colors hover:bg-error-50 hover:text-error-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-dash-on-surface-variant"
            title={assigned > 0 ? 'Reasigne los campos antes de eliminar el grupo' : 'Eliminar'}
            aria-label={`Eliminar ${group.name}`}
            disabled={assigned > 0}
            onClick={() => setDeleteOpen(true)}
          >
            <span className="material-symbols-outlined text-xl" aria-hidden="true">
              delete
            </span>
          </button>
        ) : null}
      </div>

      <ConfirmModal
        open={deleteOpen}
        title="Eliminar grupo"
        description={`¿Está seguro de eliminar el grupo «${group.name}»?`}
        confirmLabel="Eliminar"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
        onClose={() => setDeleteOpen(false)}
      />
    </>
  );
}

export function UserCustomFieldGroupsPage() {
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission(PERMISSIONS.CAMPOS_USUARIO_CREAR);

  const { data: groups, isLoading, error } = useQuery({
    queryKey: ['user-custom-field-groups'],
    queryFn: () => api<FieldGroup[]>('/user-custom-fields/groups'),
  });

  const rows = useMemo(() => groups ?? [], [groups]);

  return (
    <div className="lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-4">
          <HubBackLink to="/campos-personalizados" label="Volver a campos del perfil de usuario" />
          <div className="max-w-2xl">
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Grupos de campos</h1>
            <p className="mt-1 text-dash-on-surface-variant">
              Organice los campos personalizados en grupos. Cada campo pertenece a un grupo.
            </p>
          </div>
        </div>
        {canCreate ? (
          <div className="flex flex-wrap items-center gap-3 lg:shrink-0 lg:pl-14">
            <Link
              to="/campos-personalizados/grupos/create"
              className="inline-flex items-center gap-2 rounded-lg bg-dash-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:opacity-90"
            >
              <span className="material-symbols-outlined text-xl" aria-hidden="true">
                add
              </span>
              Nuevo grupo
            </Link>
          </div>
        ) : null}
      </div>

      {error && (
        <p className="mb-4 text-sm text-error-600" role="alert">
          {(error as Error).message}
        </p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : rows.length === 0 ? (
        <div className="lms-credentials-glass-panel overflow-hidden rounded-xl border border-dash-outline-variant shadow-sm">
          <div className="px-6 py-16 text-center">
            <p className="text-lg font-medium text-dash-on-surface">Sin grupos</p>
            <p className="mt-2 text-sm text-dash-on-surface-variant">Cree un grupo para clasificar los campos personalizados.</p>
            {canCreate ? (
              <Link
                to="/campos-personalizados/grupos/create"
                className="mt-6 inline-flex items-center gap-2 rounded-lg bg-dash-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:opacity-90"
              >
                <span className="material-symbols-outlined text-xl" aria-hidden="true">
                  add
                </span>
                Nuevo grupo
              </Link>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="lms-credentials-glass-panel overflow-hidden rounded-xl border border-dash-outline-variant shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-dash-outline-variant bg-secondary-50/80">
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-dash-on-surface-variant">Nombre</th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-dash-on-surface-variant">Campos</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-dash-on-surface-variant">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dash-outline-variant">
                {rows.map((group) => {
                  const count = fieldsCount(group);
                  return (
                    <tr key={group.id} className="transition-colors hover:bg-dash-primary/[0.02]">
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-2">
                          <span className={`h-2 w-2 shrink-0 rounded-full ${groupDotClass(group.id)}`} aria-hidden="true" />
                          <span className="font-semibold text-dash-on-surface">{group.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <span className="inline-flex rounded-full bg-secondary-200/80 px-3 py-1 text-xs text-dash-on-surface-variant">
                          {count} {count === 1 ? 'campo' : 'campos'}
                        </span>
                      </td>
                      <td className="px-6 py-5 text-right">
                        <GroupRowActions group={group} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
